<script>
  import { onMount } from 'svelte';

  // State management
  let loading = false;
  let loadingTeachers = false;
  let loadingClasses = false;
  let error = null;
  let success = null;
  let teachers = [];
  let teacherClasses = [];
  let selectedTeacher = null;
  let selectedMonth = '';
  let selectedClassIds = [];
  let currentInvoicePdf = null;
  let invoiceData = null;
  let customSum = '';
  let isRegenerating = false;
  let isSaving = false;
  let teacherQuery = '';
  let filteredTeachers = [];
  let showTeacherDropdown = false;

  // Fetch all teachers for admin selection
  async function fetchAllTeachers() {
    loadingTeachers = true;
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      error = 'JWT token not found. Please log in.';
      loadingTeachers = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/teachers-list/?include_inactive=1', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        teachers = data.teachers || [];
        filteredTeachers = teachers;
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Failed to load teachers.';
      }
    } catch (err) {
      error = `Network error: ${err.message}`;
    } finally {
      loadingTeachers = false;
    }
  }

  // Fetch classes for selected teacher and month
  async function fetchTeacherClassesForMonth() {
    if (!selectedTeacher || !selectedMonth) {
      return;
    }

    loadingClasses = true;
    error = null;
    teacherClasses = [];
    selectedClassIds = [];

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      error = 'JWT token not found. Please log in.';
      loadingClasses = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/teacher-classes-by-month/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          month: selectedMonth
        })
      });

      if (response.ok) {
        const data = await response.json();
        teacherClasses = data.classes || [];
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Failed to load classes for selected month.';
      }
    } catch (err) {
      error = `Network error: ${err.message}`;
    } finally {
      loadingClasses = false;
    }
  }

  // Generate invoice for selected teacher and classes
  async function generateInvoice() {
    if (!selectedTeacher) {
      error = 'Please select a teacher first.';
      return;
    }
    
    if (!selectedMonth) {
      error = 'Please select a month first.';
      return;
    }

    if (selectedClassIds.length === 0) {
      error = 'Please select at least one class to generate an invoice.';
      return;
    }

    loading = true;
    error = null;
    success = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      error = 'JWT token not found. Please log in.';
      loading = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/teacher-invoice/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          class_ids: selectedClassIds
        })
      });

      if (response.ok) {
        const data = await response.json();
        currentInvoicePdf = data.pdf_base64;
        invoiceData = data;
        success = `Invoice generated successfully for ${selectedTeacher.name}! Total: €${data.sum} for ${data.class_count} classes in ${selectedMonth}.`;
        
        // Clear selections
        selectedClassIds = [];
        updateSelectAllCheckbox();
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Failed to generate invoice.';
      }
    } catch (err) {
      error = `Network error: ${err.message}`;
    } finally {
      loading = false;
    }
  }

  // Download PDF
  function downloadPdf() {
    if (currentInvoicePdf && invoiceData) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${currentInvoicePdf}`;
      link.download = `${invoiceData.teacher_name.replace(' ', '_')}_invoice.pdf`;
      link.click();
    }
  }

  // Regenerate invoice with custom sum
  async function regenerateInvoice() {
    if (!customSum || isNaN(customSum) || parseFloat(customSum) <= 0) {
      error = 'Please enter a valid positive number for the custom sum.';
      return;
    }

    isRegenerating = true;
    error = null;
    success = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      error = 'JWT token not found. Please log in.';
      isRegenerating = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/teacher-invoice/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          class_ids: selectedClassIds.length > 0 ? selectedClassIds : teacherClasses.map(c => c.id),
          custom_sum: parseFloat(customSum)
        })
      });

      if (response.ok) {
        const data = await response.json();
        currentInvoicePdf = data.pdf_base64;
        invoiceData = { ...data, sum: parseFloat(customSum) }; // Update the displayed sum
        success = `Invoice regenerated successfully with custom amount €${customSum}!`;
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Failed to regenerate invoice.';
      }
    } catch (err) {
      error = `Network error: ${err.message}`;
    } finally {
      isRegenerating = false;
    }
  }

  // Save invoice for teacher
  async function saveInvoice() {
    if (!currentInvoicePdf || !selectedTeacher || !selectedMonth) {
      error = 'Cannot save invoice: missing data.';
      return;
    }

    isSaving = true;
    error = null;
    success = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      error = 'JWT token not found. Please log in.';
      isSaving = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/save-teacher-invoice/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          month: selectedMonth,
          pdf_base64: currentInvoicePdf,
          class_ids: selectedClassIds.length > 0 ? selectedClassIds : teacherClasses.map(c => c.id),
          total_sum: invoiceData ? invoiceData.sum : 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        success = `Invoice saved successfully for ${data.teacher_name} (${data.month})!`;
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Failed to save invoice.';
      }
    } catch (err) {
      error = `Network error: ${err.message}`;
    } finally {
      isSaving = false;
    }
  }

  // Handle class selection
  function handleClassSelection(classId, checked) {
    if (checked) {
      selectedClassIds = [...selectedClassIds, classId];
    } else {
      selectedClassIds = selectedClassIds.filter(id => id !== classId);
    }
    updateSelectAllCheckbox();
  }

  // Handle select all
  function handleSelectAll(checked) {
    if (checked) {
      selectedClassIds = teacherClasses.map(c => c.id);
    } else {
      selectedClassIds = [];
    }
  }

  // Update select all checkbox state
  function updateSelectAllCheckbox() {
    const selectAllCheckbox = document.getElementById('selectAllClasses');
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = selectedClassIds.length === teacherClasses.length && teacherClasses.length > 0;
      selectAllCheckbox.indeterminate = selectedClassIds.length > 0 && selectedClassIds.length < teacherClasses.length;
    }
  }

  // Filter teachers based on search query
  function filterTeachers() {
    if (!teacherQuery.trim()) {
      filteredTeachers = teachers;
      showTeacherDropdown = false;
    } else {
      const query = teacherQuery.toLowerCase();
      filteredTeachers = teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(query) ||
        teacher.email.toLowerCase().includes(query)
      );
      // Only show dropdown if no teacher is selected and there are filtered results
      showTeacherDropdown = !selectedTeacher && filteredTeachers.length > 0;
    }
  }

  // Handle teacher selection
  function selectTeacher(teacher) {
    selectedTeacher = teacher;
    teacherQuery = teacher.name;
    showTeacherDropdown = false; // Explicitly close dropdown
    teacherClasses = [];
    selectedClassIds = [];
    currentInvoicePdf = null;
    invoiceData = null;
    success = null;
    error = null;
    customSum = '';
    
    // Force update to ensure dropdown closes
    filteredTeachers = teachers;
    
    // Fetch classes if month is also selected
    if (selectedMonth) {
      fetchTeacherClassesForMonth();
    }
  }

  // Handle month change
  function handleMonthChange() {
    teacherClasses = [];
    selectedClassIds = [];
    currentInvoicePdf = null;
    invoiceData = null;
    success = null;
    customSum = '';
    
    // Fetch classes if teacher is also selected
    if (selectedTeacher && selectedMonth) {
      fetchTeacherClassesForMonth();
    }
  }

  // Month dropdown options (inclusive range): Sep 2025 -> Dec 2026
  // Keep values in "YYYY-MM" so the backend API continues to work unchanged.
  function getMonthOptions() {
    const start = { year: 2025, monthIndex: 8 };  // September (0-based)
    const end = { year: 2026, monthIndex: 11 };   // December (0-based)

    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
    const months = [];

    let year = start.year;
    let monthIndex = start.monthIndex;

    while (year < end.year || (year === end.year && monthIndex <= end.monthIndex)) {
      const monthNumber = monthIndex + 1; // 1-12
      const value = `${year}-${String(monthNumber).padStart(2, '0')}`;
      const label = formatter.format(new Date(year, monthIndex, 1));
      months.push({ value, label });

      monthIndex += 1;
      if (monthIndex > 11) {
        monthIndex = 0;
        year += 1;
      }
    }

    return months;
  }

  // Reactive statement to filter teachers when search query changes
  $: if (teacherQuery !== undefined) {
    filterTeachers();
  }

  // Initialize
  onMount(() => {
    fetchAllTeachers();
  });
</script>

<div class="teacher-invoice-container">
  <h3>Generate Teacher Invoice</h3>
  <p class="description">Search for a teacher, select a month, and generate their invoice.</p>

  {#if error}
    <div class="error-message">{error}</div>
  {/if}

  {#if success}
    <div class="success-message">{success}</div>
  {/if}

  <!-- Step 1: Teacher Selection -->
  <div class="selection-section">
    <h4>Step 1: Select Teacher</h4>
    
    {#if loadingTeachers}
      <div class="loading-message">Loading teachers...</div>
    {:else}
      <div class="input-container">
        <input 
          type="text" 
          placeholder="Search teacher by name..."
          bind:value={teacherQuery}
          on:input={() => {
            if (selectedTeacher && teacherQuery !== selectedTeacher.name) {
              selectedTeacher = null;
              showTeacherDropdown = true;
            } else if (!selectedTeacher) {
              showTeacherDropdown = teacherQuery.trim().length > 0;
            }
          }}
          autocomplete="off"
          class="search-input"
        />
        
        {#if showTeacherDropdown}
          <ul class="autocomplete-list">
            {#each filteredTeachers.slice(0, 8) as teacher}
              <li>
                <button
                  type="button"
                  on:click={() => selectTeacher(teacher)}
                  on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
                >
                  <div class="teacher-option">
                    <span class="teacher-name">{teacher.name}</span>
                    {#if teacher.email}
                      <span class="teacher-email">{teacher.email}</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
      
      {#if selectedTeacher}
        <div class="selected-teacher">
          <div class="selected-teacher-info">
            <h5>Selected Teacher</h5>
            <p><strong>Name:</strong> {selectedTeacher.name}</p>
            {#if selectedTeacher.email}
              <p><strong>Email:</strong> {selectedTeacher.email}</p>
            {/if}
            <p><strong>Classes:</strong> {selectedTeacher.class_count}</p>
            <p><strong>Bank Account:</strong> {selectedTeacher.bank_nr}</p>
          </div>
          
          <button 
            type="button" 
            class="btn-clear-small" 
            on:click={() => { selectedTeacher = null; teacherQuery = ''; showTeacherDropdown = false; }}
          >
            Clear Selection
          </button>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Step 2: Month Selection -->
  <div class="selection-section">
    <h4>Step 2: Select Month</h4>
    <select bind:value={selectedMonth} on:change={handleMonthChange} class="month-select">
      <option value="">Choose a month...</option>
      {#each getMonthOptions() as month}
        <option value={month.value}>{month.label}</option>
      {/each}
    </select>
  </div>

  <!-- Selected Teacher and Month Info -->
  {#if selectedTeacher && selectedMonth}
    <div class="selected-info-section">
      <h4>Selected Information</h4>
      <div class="info-grid">
        <div><strong>Teacher:</strong> {selectedTeacher.name}</div>
        <div><strong>Email:</strong> {selectedTeacher.email}</div>
        <div><strong>Month:</strong> {getMonthOptions().find(m => m.value === selectedMonth)?.label}</div>
        <div><strong>Bank Account:</strong> {selectedTeacher.bank_nr}</div>
      </div>
    </div>
  {/if}

  <!-- Step 3: Class Selection (only show when teacher and month are selected) -->
  {#if selectedTeacher && selectedMonth}
    <div class="classes-section">
      <h4>Step 3: Select Classes for Invoice</h4>
      
      {#if loadingClasses}
        <div class="loading-message">Loading classes for {selectedTeacher.name} in {getMonthOptions().find(m => m.value === selectedMonth)?.label}...</div>
      {:else if teacherClasses.length > 0}
        <div class="classes-found">
          <p><strong>{teacherClasses.length} classes found</strong> for {selectedTeacher.name} in {getMonthOptions().find(m => m.value === selectedMonth)?.label}</p>
          {#if teacherClasses[0]?.teacher_price !== undefined}
            <p><small>Total month value: €{teacherClasses.reduce((sum, c) => sum + (c.teacher_price || 0), 0)}</small></p>
          {/if}
        </div>
        
        <div class="select-all-container">
          <label class="select-all-label">
            <input 
              type="checkbox" 
              id="selectAllClasses"
              on:change={(e) => handleSelectAll(e.target.checked)}
            />
            Select All Classes
          </label>
        </div>

        <div class="classes-list">
          {#each teacherClasses as classItem (classItem.id)}
            <div class="class-item">
              <label class="class-label">
                <input 
                  type="checkbox" 
                  checked={selectedClassIds.includes(classItem.id)}
                  on:change={(e) => handleClassSelection(classItem.id, e.target.checked)}
                />
                <div class="class-info">
                  <div class="class-name">{classItem.name}</div>
                  <div class="class-details">
                    <small>{classItem.display_time}</small>
                    <small>{classItem.student_count} students</small>
                    {#if classItem.teacher_price !== undefined}
                      <small class="price-info">€{classItem.teacher_price}</small>
                    {/if}
                  </div>
                </div>
              </label>
            </div>
          {/each}
        </div>

        <div class="actions-section">
          <div class="selection-summary">
            <strong>{selectedClassIds.length} classes selected</strong>
          </div>
          
          <div class="action-buttons">
            <button 
              class="btn btn-primary" 
              on:click={generateInvoice}
              disabled={loading || selectedClassIds.length === 0}
            >
              {#if loading}
                Generating Invoice...
              {:else}
                Generate Invoice ({selectedClassIds.length} classes)
              {/if}
            </button>

            {#if selectedClassIds.length > 0}
              <button 
                class="btn btn-secondary" 
                on:click={() => { selectedClassIds = []; updateSelectAllCheckbox(); }}
              >
                Clear Selection
              </button>
            {/if}
          </div>
        </div>
      {:else}
        <div class="no-classes">
          <p>No classes found for {selectedTeacher.name} in {getMonthOptions().find(m => m.value === selectedMonth)?.label}.</p>
          <small>Try selecting a different month or verify that this teacher has classes scheduled.</small>
        </div>
      {/if}
    </div>
  {:else if selectedTeacher && !selectedMonth}
    <div class="instruction-message">
      <p>👆 Please select a month to view classes for {selectedTeacher.name}</p>
    </div>
  {:else if !selectedTeacher && selectedMonth}
    <div class="instruction-message">
      <p>👆 Please select a teacher to view classes for {getMonthOptions().find(m => m.value === selectedMonth)?.label}</p>
    </div>
  {/if}

  {#if currentInvoicePdf && invoiceData}
    <div class="invoice-preview-section">
      <h4>Invoice Preview</h4>
      
      <div class="invoice-summary">
        <div class="summary-grid">
          <div class="summary-item">
            <strong>Teacher:</strong> {invoiceData.teacher_name}
          </div>
          <div class="summary-item">
            <strong>Classes:</strong> {invoiceData.class_count}
          </div>
          <div class="summary-item">
            <strong>Total Amount:</strong> €{invoiceData.sum}
          </div>
          <div class="summary-item">
            <strong>Email:</strong> {invoiceData.teacher_email}
          </div>
        </div>
      </div>

      <!-- Custom Sum Section -->
      <div class="custom-sum-section">
        <h5>Adjust Invoice Amount</h5>
        <div class="custom-sum-input-group">
          <input 
            type="number" 
            bind:value={customSum} 
            placeholder="Enter custom amount (€)" 
            min="0" 
            step="0.01"
            class="custom-sum-input"
          />
          <button 
            class="btn btn-primary" 
            on:click={regenerateInvoice} 
            disabled={isRegenerating || !customSum}
          >
            {isRegenerating ? 'Regenerating...' : 'Regenerate Invoice'}
          </button>
        </div>
        <p class="custom-sum-note">
          Enter a custom amount to override the calculated total and regenerate the invoice.
        </p>
      </div>

      <div class="pdf-preview-container">
        <iframe 
          src="data:application/pdf;base64,{currentInvoicePdf}"
          class="pdf-preview"
          title="Invoice Preview"
        ></iframe>
      </div>

      <div class="download-actions">
        <button class="btn btn-success" on:click={downloadPdf}>
          📄 Download PDF Invoice
        </button>
        
        <button 
          class="btn btn-primary" 
          on:click={saveInvoice} 
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : '💾 Save Invoice for Teacher'}
        </button>
        
        <button 
          class="btn btn-secondary" 
          on:click={() => { currentInvoicePdf = null; invoiceData = null; success = null; customSum = ''; }}
        >
          Create New Invoice
        </button>
      </div>
    </div>
  {/if}
</div>

<style>
  .teacher-invoice-container {
    margin-top: 2em;
    padding: 15px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
  }

  .teacher-invoice-container h3 {
    margin: 0 0 15px 0;
    color: #fab45a;
    font-size: 1.2em;
  }

  .teacher-invoice-container h4 {
    margin: 20px 0 10px 0;
    color: #333;
    font-size: 1.1em;
    border-bottom: 1px solid #ddd;
    padding-bottom: 5px;
  }

  .description {
    color: #666;
    margin-bottom: 20px;
    font-size: 0.9em;
  }

  .error-message {
    color: #d32f2f;
    background: #ffebee;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #d32f2f;
    margin-bottom: 20px;
    font-size: 0.9em;
  }

  .success-message {
    color: #2e7d32;
    background: #e8f5e8;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #2e7d32;
    margin-bottom: 20px;
    font-size: 0.9em;
  }

  .selection-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
  }

  .input-container {
    position: relative;
    margin-top: 15px;
  }

  .search-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
  }

  .search-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
    padding: 0;
    margin: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .autocomplete-list li {
    border-bottom: 1px solid #f0f0f0;
  }

  .autocomplete-list li:last-child {
    border-bottom: none;
  }

  .autocomplete-list button {
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .autocomplete-list button:hover {
    background-color: #f8f9fa;
  }

  .teacher-option {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
    font-size: 0.9em;
  }

  .teacher-email {
    color: #666;
    font-size: 0.8em;
  }

  .selected-teacher {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff3e0;
    border: 2px solid #fab45a;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
  }

  .selected-teacher-info h5 {
    margin: 0 0 10px 0;
    color: #fab45a;
    font-size: 1em;
  }

  .selected-teacher-info p {
    margin: 5px 0;
    font-size: 0.9em;
  }

  .selected-teacher-info p strong {
    color: #333;
  }

  .btn-clear-small {
    padding: 8px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85em;
    white-space: nowrap;
  }

  .btn-clear-small:hover {
    background-color: #545b62;
  }

  .month-select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    margin-top: 10px;
  }

  .month-select:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .selected-info-section {
    background-color: #fff3e0;
    border: 2px solid #fab45a;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
    font-size: 0.9em;
  }

  .info-grid div strong {
    color: #fab45a;
  }

  .classes-found {
    background-color: #e8f5e8;
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 15px;
    text-align: center;
  }

  .classes-found p {
    margin: 0;
    color: #2e7d32;
  }

  .price-info {
    background-color: #fab45a;
    color: black;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
  }

  .instruction-message {
    text-align: center;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #ddd;
    margin: 20px 0;
  }

  .instruction-message p {
    margin: 0;
    color: #666;
    font-size: 1em;
  }



  .loading-message {
    text-align: center;
    padding: 20px;
    color: #fab45a;
    font-size: 1em;
  }

  .classes-section {
    margin-bottom: 20px;
  }

  .select-all-container {
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .select-all-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    cursor: pointer;
    font-size: 0.9em;
  }

  .classes-list {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 10px;
    background-color: #fafafa;
  }

  .class-item {
    margin-bottom: 8px;
    padding: 10px;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #e9ecef;
    transition: background-color 0.2s;
  }

  .class-item:hover {
    background-color: #f8f9fa;
  }

  .class-item:last-child {
    margin-bottom: 0;
  }

  .class-label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    width: 100%;
  }

  .class-info {
    flex-grow: 1;
  }

  .class-name {
    font-weight: 500;
    color: #333;
    font-size: 0.9em;
    margin-bottom: 4px;
  }

  .class-details {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
  }

  .class-details small {
    color: #666;
    font-size: 0.8em;
  }

  .actions-section {
    margin-top: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .selection-summary {
    margin-bottom: 15px;
    text-align: center;
    color: #fab45a;
    font-size: 1em;
  }

  .action-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-block;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background-color: #fab45a;
    color: black;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #e09a43;
  }

  .btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #545b62;
  }

  .btn-success {
    background-color: #28a745;
    color: white;
  }

  .btn-success:hover {
    background-color: #218838;
  }

  .no-classes {
    text-align: center;
    padding: 40px 20px;
    color: #666;
    font-style: italic;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .invoice-preview-section {
    margin-top: 30px;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 2px solid #fab45a;
  }

  .invoice-summary {
    margin-bottom: 20px;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
    background-color: white;
    padding: 15px;
    border-radius: 6px;
  }

  .summary-item {
    font-size: 0.9em;
  }

  .summary-item strong {
    color: #fab45a;
  }

  .pdf-preview-container {
    margin-bottom: 20px;
  }

  .pdf-preview {
    width: 100%;
    height: 500px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background-color: white;
  }

  .download-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }

  /* Mobile responsiveness */
  @media (max-width: 600px) {
    .teacher-invoice-container {
      padding: 12px;
      margin-top: 1em;
    }

    .selection-section {
      padding: 15px;
    }

    .info-grid {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .selected-teacher {
      flex-direction: column;
      gap: 10px;
    }

    .autocomplete-list {
      max-height: 150px;
    }

    .action-buttons,
    .download-actions {
      flex-direction: column;
    }

    .btn {
      width: 100%;
      margin-bottom: 8px;
    }

    .summary-grid {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .class-details {
      flex-direction: column;
      gap: 4px;
    }

    .pdf-preview {
      height: 400px;
    }

    .classes-list {
      max-height: 300px;
    }
  }

  /* Custom Sum Section Styles */
  .custom-sum-section {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .custom-sum-section h5 {
    margin: 0 0 15px 0;
    color: #495057;
    font-size: 1.1em;
  }

  .custom-sum-input-group {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
  }

  .custom-sum-input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 16px;
    max-width: 200px;
  }

  .custom-sum-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .custom-sum-note {
    margin: 0;
    font-size: 0.85em;
    color: #6c757d;
    font-style: italic;
  }

  @media (max-width: 600px) {
    .custom-sum-input-group {
      flex-direction: column;
      align-items: stretch;
    }

    .custom-sum-input {
      max-width: none;
      margin-bottom: 10px;
    }
  }
</style> 